import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Modal, Toast } from 'antd-mobile'
import { useTranslation } from '@/i18n'
import { getPhoneOrderRecord, updatePhoneOrderStatus } from '@/api/airportTransfer'
import daohangImg from "@/assets/images/daohang.png"
import callImg from "@/assets/images/shouji2.png"
import luyinImg from "@/assets/images/luyin.png"
import './detail.scss'

const CallOrderDetail = () => {
  const history = useHistory()
  const { orderId } = useParams()
  const t = useTranslation()
  const mapRef = useRef(null)
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapError, setMapError] = useState(false)
  const [showFeeModal, setShowFeeModal] = useState(false)
  const [carFee, setCarFee] = useState('')
  
  // 订单状态：根据order.orderStatus动态显示
  const [orderStatus, setOrderStatus] = useState(1)

  // 滑块相关状态
  const [sliderOffset, setSliderOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isSliderSuccess, setIsSliderSuccess] = useState(false)
  const sliderRef = useRef(null)
  const containerRef = useRef(null)
  const startXRef = useRef(0)

  // 获取订单详情
  const fetchOrderDetail = async () => {
    setLoading(true)
    try {
      const params = {
        orderId: orderId,
        orderSource: 2, // 2-电话下单
      }

      const response = await getPhoneOrderRecord(params)

      if (response && response.data && response.data.code === 200) {
        const orderData = response.data.data.datas?.[0]
        
        if (orderData) {
          // 格式化价格
          orderData.price = orderData.price ? (orderData.price / 100).toFixed(2) : '0.00'
          
          setOrder(orderData)
          // 根据订单状态设置UI状态
          // orderStatus: 1-呼叫中, 2-接驾中, 3-司机到达上车点, 4-乘客上车, 5-已完成, 6-司机已收款
          if (orderData.orderStatus === 2) {
            setOrderStatus(1) // UI显示：待接驾（到达乘客上车点）
          } else if (orderData.orderStatus === 3) {
            setOrderStatus(2) // UI显示：已接驾（乘客已上车）
          } else if (orderData.orderStatus === 4) {
            setOrderStatus(3) // UI显示：行程结束
          }
        } else {
          Toast.fail(t('callOrder.detail.orderNotFound'), 2)
        }
      } else if (response.data && response.data.errorMsg) {
        console.error(response.data.errorMsg)
        Toast.fail(response.data.errorMsg, 2)
      }
    } catch (error) {
      console.error('获取订单详情失败', error)
      Toast.fail(t('callOrder.detail.fetchFailed'), 2)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail()
    }
  }, [orderId])

  // 初始化腾讯地图
  useEffect(() => {
    if (!order || !order.startLat || !order.destinationLat) return

    // 动态加载腾讯地图SDK
    const loadTencentMap = () => {
      // 检查TMap是否已加载 (GL版本使用TMap，不是qq.maps)
      if (window.TMap) {
        console.log('腾讯地图SDK已加载，开始初始化地图')
        setTimeout(() => initMap(), 100)
        return
      }

      console.log('开始加载腾讯地图SDK')
      const script = document.createElement('script')
      // 使用callback参数，地图加载完成后自动调用initMap
      script.src = 'https://map.qq.com/api/gljs?v=1.exp&key=ZJFBZ-PCSW3-C6H3K-O5GCL-EE5NE-TZBMM&callback=initTMap'
      
      // 将initMap函数挂载到window上，供callback调用
      window.initTMap = () => {
        console.log('腾讯地图SDK加载成功')
        setTimeout(() => initMap(), 100)
      }
      
      script.onerror = () => {
        console.error('腾讯地图SDK加载失败')
        setMapError(true)
      }
      document.head.appendChild(script)
    }

    const initMap = () => {
      if (!mapRef.current) {
        console.error('地图容器不存在')
        setMapError(true)
        return
      }
      
      if (!window.TMap) {
        console.error('腾讯地图API未加载')
        setMapError(true)
        return
      }

      console.log('开始初始化地图', { order })

      try {
        // 使用订单的经纬度，如果没有则使用默认值
        const startLat = order.startLat || 39.9042
        const startLng = order.startLng || 116.4074
        const endLat = order.destinationLat || 39.9142
        const endLng = order.destinationLng || 116.4174
        
        // 计算中心点 - 注意：TMap使用 LatLng(纬度, 经度)
        const centerLat = (startLat + endLat) / 2
        const centerLng = (startLng + endLng) / 2

        // 创建地图实例 - 使用TMap.Map
        const map = new window.TMap.Map(mapRef.current, {
          center: new window.TMap.LatLng(centerLat, centerLng),
          zoom:11,
        })

        console.log('地图实例创建成功')

        // 创建MultiMarker实例添加起点和终点标记
        const markerLayer = new window.TMap.MultiMarker({
          map: map,
          styles: {
            'start-marker': new window.TMap.MarkerStyle({
              width: 25,
              height: 35,
              anchor: { x: 12, y: 35 },
              src: 'https://mapapi.qq.com/web/lbs/javascriptGL/demo/img/start.png',
            }),
            'end-marker': new window.TMap.MarkerStyle({
              width: 25,
              height: 35,
              anchor: { x: 12, y: 35 },
              src: 'https://mapapi.qq.com/web/lbs/javascriptGL/demo/img/end.png',
            }),
          },
          geometries: [
            {
              id: 'start',
              styleId: 'start-marker',
              position: new window.TMap.LatLng(startLat, startLng),
            },
            {
              id: 'end',
              styleId: 'end-marker',
              position: new window.TMap.LatLng(endLat, endLng),
            },
          ],
        })

        console.log('标记点添加成功')

        // 调整地图视野以包含两个点
        const bounds = new window.TMap.LatLngBounds()
        bounds.extend(new window.TMap.LatLng(startLat, startLng))
        bounds.extend(new window.TMap.LatLng(endLat, endLng))
        map.fitBounds(bounds, {
          padding: 100, // 添加内边距
        })

        setMapLoaded(true)
        console.log('地图初始化完成')
      } catch (error) {
        console.error('地图初始化出错:', error)
        setMapError(true)
      }
    }

    loadTencentMap()
    
    // 清理函数
    return () => {
      if (window.initTMap) {
        delete window.initTMap
      }
    }
  }, [order])

  // 导航按钮 - 根据订单状态决定导航到起点还是终点
  const handleNavigation = () => {
    if (!order) {
      Toast.info(t('callOrder.detail.orderInfoMissing'), 2)
      return
    }

    // 根据订单状态判断导航目的地
    // orderStatus: 1-呼叫中, 2-接驾中(去起点), 3-司机到达上车点, 4-乘客上车(去终点), 5-已完成, 6-司机已收款
    // 当订单状态为2或3时，导航到起点（乘客上车点）
    // 当订单状态为4时，导航到终点
    const shouldNavigateToStart = order.orderStatus === 2 || order.orderStatus === 3

    if (shouldNavigateToStart) {
      // 导航到起点（乘客上车点）
      if (order.startLat && order.startLng) {
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${order.startLat},${order.startLng}&travelmode=driving`
        window.open(googleMapsUrl, '_blank')
      } else if (order.startName) {
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(order.startName)}&travelmode=driving`
        window.open(googleMapsUrl, '_blank')
      } else {
        Toast.info(t('callOrder.detail.noPickupInfo'), 2)
      }
    } else {
      // 导航到终点
      if (order.destinationLat && order.destinationLng) {
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${order.destinationLat},${order.destinationLng}&travelmode=driving`
        window.open(googleMapsUrl, '_blank')
      } else if (order.destinationName) {
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(order.destinationName)}&travelmode=driving`
        window.open(googleMapsUrl, '_blank')
      } else {
        Toast.info(t('callOrder.detail.noDestinationInfo'), 2)
      }
    }
  }

  // 联系乘客
  const handleCall = () => {
    if (order && order.passengerPhone) {
      window.location.href = `tel:${order.passengerPhone}`
    }
  }

  // 重播语音
  const handleVoice = () => {
    if (order && order.voiceUrl) {
      const audio = new Audio(order.voiceUrl)
      audio.play().catch(error => {
        console.error('播放语音失败:', error)
        Toast.fail(t('callOrder.detail.voicePlayFailed'), 2)
      })
    } else {
      Toast.info(t('callOrder.detail.noVoiceInfo'), 2)
    }
  }

  // 更新订单状态
  const updateStatus = async (status) => {
    Toast.loading(t('callOrder.detail.processing'), 0, () => {}, true)
    try {
      const response = await updatePhoneOrderStatus(orderId, status.toString())
      Toast.hide()
      if (response && response.data && response.data.code === 200) {
        Toast.success(t('callOrder.detail.operationSuccess'))
        // 刷新订单详情
        fetchOrderDetail()
      } else {
        console.error('更新订单状态失败', response)
        Toast.fail(t('callOrder.detail.updateFailed') + ': ' + (response?.data?.errorMsg || t('airportTransfer.error.unknown')), 2)
      }
    } catch (error) {
      console.error('更新订单状态出错', error)
      Toast.hide()
      Toast.fail(t('callOrder.detail.updateError'), 2)
    }
  }

  // 到达乘客上车点
  const handleArrivePickup = () => {
    Modal.alert(t('callOrder.detail.confirm'), t('callOrder.detail.arrivedPickupConfirm'), [
      { text: t('callOrder.detail.cancel'), onPress: () => {} },
      {
        text: t('callOrder.detail.confirm'),
        onPress: () => {
          // orderStatus 3 表示司机到达上车点
          updateStatus(3)
          setOrderStatus(2)
        }
      }
    ])
  }

  // 乘客已上车（开始计价）
  const handleStartTrip = () => {
    Modal.alert(t('callOrder.detail.confirm'), t('callOrder.detail.passengerBoardedConfirm'), [
      { text: t('callOrder.detail.cancel'), onPress: () => {} },
      {
        text: t('callOrder.detail.confirm'),
        onPress: () => {
          // orderStatus 4 表示乘客上车
          updateStatus(4)
          setOrderStatus(3)
        }
      }
    ])
  }

  // 行程结束，确认费用
  const handleEndTrip = () => {
    // 初始化车费为订单价格
    setCarFee(order.price || '')
    setShowFeeModal(true)
  }

  // 提交费用 - 未收现金
  const handleSubmitFeeNoWait = async () => {
    if (!carFee || parseFloat(carFee) <= 0) {
      Modal.alert(t('callOrder.detail.hint'), t('callOrder.detail.invalidFeeAmount'))
      return
    }

    Modal.alert(t('callOrder.detail.confirm'), t('callOrder.detail.confirmNoCash'), [
      { text: t('callOrder.detail.cancel'), onPress: () => {} },
      {
        text: t('callOrder.detail.confirm'),
        onPress: async () => {
          Toast.loading(t('callOrder.detail.processing'), 0, () => {}, true)
          try {
            // 将金额转换为分（整数）
            const priceInCents = Math.round(parseFloat(carFee) * 100)
            
            const response = await updatePhoneOrderStatus(orderId, '5', priceInCents)
            Toast.hide()
            
            if (response && response.data && response.data.code === 200) {
              Toast.success(t('callOrder.detail.paymentInitiated'))
              setShowFeeModal(false)
              setTimeout(() => {
                history.goBack()
              }, 1500)
            } else {
              console.error('提交费用失败', response)
              Toast.fail(t('callOrder.detail.submitFailed') + ': ' + (response?.data?.errorMsg || t('airportTransfer.error.unknown')), 2)
            }
          } catch (error) {
            console.error('提交费用出错', error)
            Toast.hide()
            Toast.fail(t('callOrder.detail.submitError'), 2)
          }
        }
      }
    ])
  }

  // 提交费用 - 已现金收款
  const handleSubmitFeeReceived = async () => {
    if (!carFee || parseFloat(carFee) <= 0) {
      Modal.alert(t('callOrder.detail.hint'), t('callOrder.detail.invalidFeeAmount'))
      return
    }

    Modal.alert(t('callOrder.detail.confirm'), t('callOrder.detail.confirmCashReceived'), [
      { text: t('callOrder.detail.cancel'), onPress: () => {} },
      {
        text: t('callOrder.detail.confirm'),
        onPress: async () => {
          Toast.loading(t('callOrder.detail.processing'), 0, () => {}, true)
          try {
            // 将金额转换为分（整数）
            const priceInCents = Math.round(parseFloat(carFee) * 100)
            
            const response = await updatePhoneOrderStatus(orderId, '5', priceInCents)
            Toast.hide()
            
            if (response && response.data && response.data.code === 200) {
              Toast.success(t('callOrder.detail.orderCompleted'))
              setShowFeeModal(false)
              setTimeout(() => {
                history.goBack()
              }, 1500)
            } else {
              console.error('提交费用失败', response)
              Toast.fail(t('callOrder.detail.submitFailed') + ': ' + (response?.data?.errorMsg || t('airportTransfer.error.unknown')), 2)
            }
          } catch (error) {
            console.error('提交费用出错', error)
            Toast.hide()
            Toast.fail(t('callOrder.detail.submitError'), 2)
          }
        }
      }
    ])
  }

  // 复制订单号
  const handleCopy = () => {
    if (order && order.orderId) {
      // 创建临时输入框
      const input = document.createElement('input')
      input.value = order.orderId
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      Toast.info(t('callOrder.detail.orderIdCopied'), 2)
    }
  }

  // 滑块开始滑动
  const handleSliderStart = (e) => {
    if (isSliderSuccess) return
    setIsDragging(true)
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
    startXRef.current = clientX - sliderOffset
    
    // 阻止默认行为，防止页面滚动
    if (e.type === 'touchstart') {
      e.preventDefault()
    }
  }

  // 滑块滑动中
  const handleSliderMove = (e) => {
    if (!isDragging || isSliderSuccess) return
    
    e.preventDefault()
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
    const containerWidth = containerRef.current?.offsetWidth || 0
    const sliderWidth = sliderRef.current?.offsetWidth || 0
    const maxOffset = containerWidth - sliderWidth
    
    let newOffset = clientX - startXRef.current
    newOffset = Math.max(0, Math.min(newOffset, maxOffset))
    
    setSliderOffset(newOffset)
  }

  // 滑块结束滑动
  const handleSliderEnd = () => {
    if (!isDragging || isSliderSuccess) return
    
    const containerWidth = containerRef.current?.offsetWidth || 0
    const sliderWidth = sliderRef.current?.offsetWidth || 0
    const maxOffset = containerWidth - sliderWidth
    
    // 判断是否滑动到80%以上
    if (sliderOffset >= maxOffset * 0.8) {
      // 滑动成功，触发对应操作
      setSliderOffset(maxOffset)
      setIsSliderSuccess(true)
      setTimeout(() => {
        handleSliderComplete()
      }, 300)
    } else {
      // 未滑动到位，回弹
      setSliderOffset(0)
    }
    
    setIsDragging(false)
  }

  // 滑块完成操作
  const handleSliderComplete = () => {
    if (orderStatus === 1) {
      Modal.alert(t('callOrder.detail.confirm'), t('callOrder.detail.arrivedPickupConfirm'), [
        { 
          text: t('callOrder.detail.cancel'), 
          onPress: () => {
            setSliderOffset(0)
            setIsSliderSuccess(false)
          }
        },
        {
          text: t('callOrder.detail.confirm'),
          onPress: () => {
            updateStatus(3) // orderStatus 3 表示司机到达上车点
            setOrderStatus(2)
            setSliderOffset(0)
            setIsSliderSuccess(false)
          }
        }
      ])
    } else if (orderStatus === 2) {
      Modal.alert(t('callOrder.detail.confirm'), t('callOrder.detail.passengerBoardedConfirm'), [
        { 
          text: t('callOrder.detail.cancel'), 
          onPress: () => {
            setSliderOffset(0)
            setIsSliderSuccess(false)
          }
        },
        {
          text: t('callOrder.detail.confirm'),
          onPress: () => {
            updateStatus(4) // orderStatus 4 表示乘客上车
            setOrderStatus(3)
            setSliderOffset(0)
            setIsSliderSuccess(false)
          }
        }
      ])
    } else if (orderStatus === 3) {
      setSliderOffset(0)
      setIsSliderSuccess(false)
      // 初始化车费为订单价格
      setCarFee(order.price || '')
      setShowFeeModal(true)
    }
  }

  // 获取滑块文字
  const getSliderText = () => {
    if (isSliderSuccess) {
      return t('callOrder.detail.operationSuccessful')
    }
    switch (orderStatus) {
      case 1:
        return t('callOrder.detail.slideArrivePickup')
      case 2:
        return t('callOrder.detail.slidePassengerBoarded')
      case 3:
        return t('callOrder.detail.slideEndTrip')
      default:
        return t('callOrder.detail.slideOperation')
    }
  }

  // 计算滑动进度百分比
  const getSliderProgress = () => {
    const containerWidth = containerRef.current?.offsetWidth || 0
    const sliderWidth = sliderRef.current?.offsetWidth || 0
    const maxOffset = containerWidth - sliderWidth
    return maxOffset > 0 ? (sliderOffset / maxOffset) * 100 : 0
  }

  if (loading) {
    return <div className="loading-container">{t('callOrder.detail.loading')}</div>
  }

  if (!order) {
    return <div className="error-container">{t('callOrder.detail.orderNotFound')}</div>
  }

  return (
    <div className="call-order-detail">
      {/* 订单信息顶部 */}
      <div className="order-info-header">
        <div className="info-item">
          <i className="icon-start"></i>
          <span>{order.startName}</span>
        </div>
        <div className="info-item">
          <i className="icon-end"></i>
          <span>{order.destinationName}</span>
        </div>
        <div className="info-item">
          <i className="icon-time"></i>
          <span>{order.departureTime}</span>
        </div>

        {/* 操作按钮 */}
        <div className="action-buttons">
          <div className="action-btn nav" onClick={handleNavigation}>
            <div className="btn-icon">
              <img src={daohangImg} alt={t('callOrder.detail.navigation')} />
            </div>
            <span>{t('callOrder.detail.navigation')}</span>
          </div>
          
          <div className="action-btn call" onClick={handleCall}>
            <div className="btn-icon">
              <img src={callImg} alt={t('callOrder.detail.contactPassenger')} />
            </div>
            <div className="btn-content">
              <span className="label">{t('callOrder.detail.contactPassenger')}</span>
            </div>
          </div>
          
          <div className="action-btn voice" onClick={handleVoice}>
            <div className="btn-icon">
              <img src={luyinImg} alt={t('callOrder.detail.replayVoice')} />
            </div>
            <span>{t('callOrder.detail.replayVoice')}</span>
          </div>
        </div>
      </div>

      {/* 地图容器 */}
      <div 
        id="tencent-map" 
        className="map-container" 
        ref={mapRef}
        style={{ 
          position: 'relative',
          background: mapLoaded ? 'transparent' : '#e5e5e5'
        }}
      >
        {!mapLoaded && !mapError && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#999',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            <div>{t('callOrder.detail.mapLoading')}</div>
            <div style={{ fontSize: '12px', marginTop: '10px' }}>
              {t('callOrder.detail.mapLoadingHint')}
            </div>
          </div>
        )}
        {mapError && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#999',
            fontSize: '14px',
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{ marginBottom: '10px' }}>🗺️</div>
            <div>{t('callOrder.detail.mapLoadFailed')}</div>
            <div style={{ fontSize: '12px', marginTop: '10px', color: '#ccc' }}>
              {t('callOrder.detail.startPoint')}: {order.startName}<br/>
              {t('callOrder.detail.endPoint')}: {order.destinationName}
            </div>
          </div>
        )}
      </div>

      {/* 底部滑块操作 - 仅待服务订单显示 */}
      {order && order.orderStatus >= 2 && order.orderStatus <= 4 && (
        <div className="bottom-action">
          <div 
            className={`slider-container ${isDragging ? 'dragging' : ''} ${isSliderSuccess ? 'success' : ''}`}
            ref={containerRef}
          >
            <div 
              className="slider-progress" 
              style={{ 
                width: `${getSliderProgress()}%`,
                opacity: isDragging || isSliderSuccess ? 1 : 0
              }}
            />
            <div className="slider-text">
              {getSliderText()}
            </div>
            <div
              className="slider-button"
              ref={sliderRef}
              style={{ 
                transform: `translateX(${sliderOffset}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease'
              }}
              onMouseDown={handleSliderStart}
              onMouseMove={handleSliderMove}
              onMouseUp={handleSliderEnd}
              onMouseLeave={handleSliderEnd}
              onTouchStart={handleSliderStart}
              onTouchMove={handleSliderMove}
              onTouchEnd={handleSliderEnd}
            >
              <span className={`slider-icon ${isSliderSuccess ? 'success' : ''}`}>
                {isSliderSuccess ? '✓' : '›››'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 费用确认弹窗 */}
      <Modal
        visible={showFeeModal}
        transparent
        maskClosable={false}
        title=""
        popup
        footer={[]}
        wrapProps={{ onTouchStart: () => {} }}
        className="fee-modal"
        style={{ zIndex: 10000 }}
      >
        <div 
          className="fee-modal-content"
          style={{
            padding: '28px 20px 24px 20px',
            minWidth: '320px',
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: '12px',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '18px'
          }}
        >
            <div 
              className="warning-text"
              style={{
                color: '#ff9800',
                fontSize: '15px',
                marginBottom: '8px',
                textAlign: 'center',
                fontWeight: 500,
                lineHeight: '1.5'
              }}
            >
              {t('callOrder.detail.paymentWarning')}
            </div>          <div 
            className="fee-input-container"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '8px',
              width: '100%',
              justifyContent: 'center'
            }}
          >
            <label style={{ fontSize: '16px', fontWeight: 500 }}>{t('callOrder.detail.fee')}</label>
            <input
              type="number"
              placeholder={t('callOrder.detail.enterFee')}
              value={carFee}
              onChange={(e) => setCarFee(e.target.value)}
              style={{
                width: '90px',
                fontSize: '16px',
                padding: '6px 8px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                marginLeft: '8px',
                marginRight: '8px',
                textAlign: 'center'
              }}
            />
            <span className="unit" style={{ fontSize: '16px', color: '#333' }}>{t('callOrder.detail.yuan')}</span>
          </div>
          
          
            <div 
              className="fee-note"
              style={{
                fontSize: '13px',
                color: '#999',
                marginBottom: '10px',
                textAlign: 'center'
              }}
            >
              {t('callOrder.detail.feeConfirmationNote')}
            </div>          <button 
            className="fee-button primary"
            onClick={handleSubmitFeeNoWait}
            style={{
              width: '100%',
              padding: '12px 0',
              fontSize: '16px',
              borderRadius: '8px',
              background: '#1677ff',
              color: '#fff',
              fontWeight: 500,
              marginBottom: '10px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {t('callOrder.detail.noCashPayment')}
          </button>
          
          <button 
            className="fee-button"
            onClick={handleSubmitFeeReceived}
            style={{
              width: '100%',
              padding: '12px 0',
              fontSize: '16px',
              borderRadius: '8px',
              background: '#f5f5f5',
              color: '#333',
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {t('callOrder.detail.cashReceived')}
          </button>
        </div>
      </Modal>

      {/* 已完成状态显示 */}
      {(order.orderStatus === 5 || order.orderStatus === 6) && (
        <div className="completed-info">
          <div className="order-number">
            <span>{t('callOrder.detail.orderNumber')}{order.orderId}</span>
            <button className="copy-btn" onClick={handleCopy}>{t('callOrder.detail.copy')}</button>
          </div>
          <div className="total-fee">
            <span className="amount">{order.price}</span>
            <span className="unit">{t('callOrder.detail.yuan')}</span>
            <span className="status">{t('callOrder.detail.completed')}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default CallOrderDetail
